/**
=========================================================
* Call It In LLC - Courier Dashboard
=========================================================

* Company: Call It In LLC
* Website: https://www.justcallitin.com
* Author: Shaun Matthews
* Copyright: © 2025 Call It In LLC. All rights reserved.

=========================================================

* This file is part of the Call It In dashboard system.
* Any reproduction or distribution without written consent is strictly prohibited.
*/


import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

import Sidenav from "examples/Sidenav";
// Remove the Material UI configurator. The dashboard no longer uses the side
// drawer configurator provided by the original Material Dashboard template.
// import Configurator from "examples/Configurator";

import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import routes from "routes";

import { getCurrentRole } from "data/users";
import { AuthGuard } from "components/AuthGuard";
import { getBackendCapabilities, SESSION_EXPIRED_EVENT } from "api/client";
import { filterRoutesForRole, getDefaultRouteForRole } from "utils/routeAccess";

import {
  useMaterialUIController,
  setMiniSidenav,
  // setOpenConfigurator, // no longer used after removing configurator
} from "context";

// Images
// Import the Call It In logo for both light and dark themes. The original
// template referenced Creative Tim branding (logo-ct.*). Per rebranding
// requirements, use Call It In’s logo instead. We provide the same logo
// regardless of theme since a dark variant isn’t currently available.
import brandWhite from "assets/images/callitinlogo.png";
import brandDark from "assets/images/callitinlogo.png";

const ACCESS_NOTICE_KEY = "ci_access_notice";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const [systemNotice, setSystemNotice] = useState(null);
  const { pathname } = useLocation();

  // ── Auth state ────────────────────────────────────────────────────────────
  // Read the JWT session on every render so route changes always reflect the
  // current authentication state.  No dummy users or local fallbacks exist —
  // if isAuthenticated() is false the user has never successfully hit
  // /auth/login on the live backend.
  const role = getCurrentRole();
  const isDriver = role === "driver";
  const wildcardTarget = getDefaultRouteForRole(role);
  const showSidenav = layout === "dashboard" && !isDriver && pathname !== "/login";
  const navRoutes = useMemo(() => filterRoutesForRole(routes, role, { forNavigation: true }), [role]);
  const routableRoutes = useMemo(() => filterRoutesForRole(routes, role), [role]);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    const handleSessionExpired = (event) => {
      const detail = event?.detail || {};
      setSystemNotice({
        severity: "warning",
        message: detail.message || "Your session expired. Please sign in again.",
      });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(ACCESS_NOTICE_KEY);
      if (!raw) return;
      window.sessionStorage.removeItem(ACCESS_NOTICE_KEY);
      const detail = JSON.parse(raw);
      if (detail?.message) {
        setSystemNotice({ severity: "warning", message: detail.message });
      }
    } catch {
      // ignore storage failures
    }
  }, [pathname]);

  useEffect(() => {
    if (!role) return;

    let cancelled = false;
    const noticeKey = "ci_capability_notice_key";

    (async () => {
      const caps = await getBackendCapabilities().catch(() => null);
      if (cancelled || !caps?.routes) return;

      const critical = [
        ["taskStatusUpdate", "task status updates"],
        ["taskPodSubmit", "proof of delivery"],
        ["taskOverrideRequest", "override requests"],
        ["taskOverrideApprove", "override approvals"],
        ["taskOverrideDeny", "override denials"],
        ["driverTasks", "driver task feed"],
      ];
      const missing = critical.filter(([key]) => !caps.routes[key]).map(([, label]) => label);
      if (!missing.length) return;

      const fingerprint = missing.join("|");
      try {
        if (window.sessionStorage.getItem(noticeKey) === fingerprint) return;
        window.sessionStorage.setItem(noticeKey, fingerprint);
      } catch {
        // ignore storage failures
      }

      setSystemNotice({
        severity: "warning",
        message: `Backend deployment is missing required routes: ${missing.join(", ")}.`,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [role]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }

      return null;
    });

  // Floating settings button removed. If you want to restore the button,
  // uncomment the following definition and re‑introduce it into the layout:
  // const configsButton = (
  //   <MDBox
  //     display="flex"
  //     justifyContent="center"
  //     alignItems="center"
  //     width="3.25rem"
  //     height="3.25rem"
  //     bgColor="white"
  //     shadow="sm"
  //     borderRadius="50%"
  //     position="fixed"
  //     right="2rem"
  //     bottom="2rem"
  //     zIndex={99}
  //     color="dark"
  //     sx={{ cursor: "pointer" }}
  //     onClick={handleConfiguratorOpen}
  //   >
  //     <Icon fontSize="small" color="inherit">
  //       settings
  //     </Icon>
  //   </MDBox>
  // );

  return (
    <AuthGuard>
      {direction === "rtl" ? (
        <CacheProvider value={rtlCache}>
          <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
            <CssBaseline />
            {showSidenav && (
              <Sidenav
                color={sidenavColor}
                brand={
                  (transparentSidenav && !darkMode) || whiteSidenav
                    ? brandDark
                    : brandWhite
                }
                brandName="Call It In"
                routes={navRoutes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
            )}
            <Routes>
              {getRoutes(routableRoutes)}
              <Route path="*" element={<Navigate to={wildcardTarget} replace />} />
            </Routes>
            <Snackbar
              open={!!systemNotice}
              autoHideDuration={6000}
              onClose={() => setSystemNotice(null)}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Alert
                severity={systemNotice?.severity || "info"}
                variant="filled"
                onClose={() => setSystemNotice(null)}
                sx={{ width: "100%" }}
              >
                {systemNotice?.message || ""}
              </Alert>
            </Snackbar>
          </ThemeProvider>
        </CacheProvider>
      ) : (
        <ThemeProvider theme={darkMode ? themeDark : theme}>
          <CssBaseline />
          {showSidenav && (
            <Sidenav
              color={sidenavColor}
              brand={
                (transparentSidenav && !darkMode) || whiteSidenav
                  ? brandDark
                  : brandWhite
              }
              brandName="Call It In"
              routes={navRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          )}
          <Routes>
            {getRoutes(routableRoutes)}
            <Route path="*" element={<Navigate to={wildcardTarget} replace />} />
          </Routes>
          <Snackbar
            open={!!systemNotice}
            autoHideDuration={6000}
            onClose={() => setSystemNotice(null)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              severity={systemNotice?.severity || "info"}
              variant="filled"
              onClose={() => setSystemNotice(null)}
              sx={{ width: "100%" }}
            >
              {systemNotice?.message || ""}
            </Alert>
          </Snackbar>
        </ThemeProvider>
      )}
    </AuthGuard>
  );
}
